// Pure text builders for the delivery performance chart: the tooltip line, the
// chart's spoken label, and the not-yet-due remainder a column cannot show.
//
// They sit in their own leaf module because Recharts never calls a tooltip
// renderer under jsdom's zero-width ResponsiveContainer, so wording left
// inside the chart component would be untestable. Here it is covered directly
// and the component holds only the plumbing.
//
// The tooltip reports the `due` total the server sent and never a sum of the
// three outcome segments. Items still inside their due date carry no outcome,
// so in the current month the segments stack short of `due` — summing them
// would under-report the month's commitments to the one reader checking them.

import type { DeliveryBucket } from "../../../../types/report";
import { formatReportMonth } from "../reportFormat";

/** One line, e.g. "Aug 2026 · 4 due · 1 on time · 1 late · 2 still overdue".
 *  A month with nothing due says so instead of listing four zeros. */
export function formatDeliveryTooltip(bucket: DeliveryBucket): string {
  const month = formatReportMonth(bucket.month);
  if (bucket.due === 0) return `${month} · nothing due`;
  return [
    month,
    `${bucket.due} due`,
    `${bucket.onTime} on time`,
    `${bucket.late} late`,
    `${bucket.stillOverdue} still overdue`,
  ].join(" · ");
}

/** The chart's `aria-label`: the window, the range totals, and a pointer to
 *  the table that carries the per-month figures. The table is the accessible
 *  path to exact values, so the label deliberately does not read out twelve
 *  months of numbers. */
export function describeDeliveryChart(buckets: DeliveryBucket[]): string {
  const first = formatReportMonth(buckets[0].month);
  const last = formatReportMonth(buckets[buckets.length - 1].month);
  const total = (key: "onTime" | "late" | "stillOverdue"): number =>
    buckets.reduce((sum, bucket) => sum + bucket[key], 0);
  return (
    `Delivery outcomes by due month, ${first} to ${last}: ` +
    `${total("onTime")} delivered on time, ${total("late")} delivered late, ` +
    `${total("stillOverdue")} still overdue. ` +
    `Exact monthly figures follow in the breakdown table.`
  );
}

/** Items due in the month that are still inside their due date, so they have
 *  no outcome yet and appear in no column segment. Only the current month can
 *  hold any: an earlier month's undelivered item is already overdue. */
export function notYetDueCount(bucket: DeliveryBucket): number {
  return bucket.due - bucket.onTime - bucket.late - bucket.stillOverdue;
}
